import { IS_CLINIC, LABELS } from "../platform/config";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  InputAdornment,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PageHeader from "../components/PageHeader";
import VenueCover from "../components/venues/VenueCover";
import { COLORS } from "../assets/theme";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import { getAllBusinesses, createBusiness } from "../services/VenueService";
import AddressWithMapField from "../components/map/AddressWithMapField";

const Home: React.FC = () => {
  const [venues, setVenues] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const fields: Field[] = [
    {
      type: "text" as const,
      name: "name",
      label: "Business Name",
      required: true,
    },
    {
      type: "number" as const,
      name: "orderNumber",
      label: "Order Number",
    },
    { type: "file" as const, name: "banner", label: `${LABELS.venue} Banner` },
    {
      type: "text" as const,
      name: "overLay",
      label: "Overlay Text",
    },
    {
      type: "text" as const,
      name: "insta_url",
      label: "Instagram URL",
    },
    {
      name: "address",
      label: "Address",
      colSpan: 2,
      customRender: (
        value: any,
        onChange: (v: any) => void,
        helpers?: {
          setFieldValue: (name: string, value: any) => void;
          values?: Record<string, any>;
        }
      ) => (
        <AddressWithMapField value={value} onChange={onChange} helpers={helpers} />
      ),
    },
  ];

  const handleAddVenue = () => {
    setDialogOpen(true);
  };

 const handleDialogSubmit = async (values: Record<string, any>) => {
  try {
    setLoading(true);

    const parsedOrderNumber =
      values.orderNumber !== undefined && values.orderNumber !== ""
        ? parseInt(String(values.orderNumber), 10)
        : undefined;

    await createBusiness({
      name: values.name,
      banner: values.banner,
      address: values.address || "",
      latitude: values.latitude || "",
      longitude: values.longitude || "",
      ...(parsedOrderNumber !== undefined && !Number.isNaN(parsedOrderNumber)
        ? { orderNumber: parsedOrderNumber }
        : {}),
      overLay: values.overLay,
      insta_url: values.insta_url,
    });

    const updated = await getAllBusinesses();
    setVenues(Array.isArray(updated) ? mapApiBusinesses(updated) : []);
    setDialogOpen(false);
  } catch (error: any) {
    const msg =
    error?.response?.data?.message ||
    "Failed to create business";

  console.error(error);
  
  } finally {
    setLoading(false);
  }
};


  const navigate = useNavigate();

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const data = await getAllBusinesses();
        const list = Array.isArray(data) ? data : [];
        // Most clinics have one location: skip the picker and open its Today
        // page. "All clinics" in the clinic menu comes back here with ?all=1.
        const showAll = new URLSearchParams(window.location.search).get("all") === "1";
        if (IS_CLINIC && !showAll && list.length === 1 && list[0]?.id) {
          navigate(`/dashboard/${list[0].id}`, { replace: true });
          return;
        }
        setVenues(mapApiBusinesses(list));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [navigate]);

  const handleBusinessClick = (business: {
    id?: string;
    name: string;
    image: string;
    user_id?: string;
  }) => {
    const businessId = business.id || business.user_id || "unknown";
    navigate(`/dashboard/${businessId}`, { state: { business, businessId } });
  };
function mapApiBusinesses(items: any[]) {
  return items.map((v) => ({
    id: v.id,
    name: v.name?.trim() || "Unnamed Business",
    image: (v.banner ?? v.image ?? v.image_url)?.trim() || "/favicon.ico",
    orderNumber: v.orderNumber,
  }));
}

  const query = search.trim().toLowerCase();
  const visibleVenues = query
    ? venues.filter((v) => (v.name || "").toLowerCase().includes(query))
    : venues;
  const initialLoad = loading && venues.length === 0 && !dialogOpen;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: COLORS.MainBackground }}>
      <Box
        sx={{
          maxWidth: 1360,
          mx: "auto",
          px: { xs: 2, sm: 4 },
          py: { xs: 3, sm: 5 },
        }}
      >
        <PageHeader
          title={`My ${LABELS.venues}`}
          subtitle={`Choose a ${LABELS.venue.toLowerCase()} to open its dashboard.`}
          icon={<StorefrontOutlinedIcon />}
          actions={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddVenue}
              disableElevation
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2.5, px: 2.5 }}
            >
              Add {LABELS.venue.toLowerCase()}
            </Button>
          }
          sx={{ mb: 3 }}
        />
        <GenericFormDialog
          open={dialogOpen}
          title={`Add New ${LABELS.venue}`}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleDialogSubmit}
          fields={fields}
        />

        {venues.length > 4 && (
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${LABELS.venues.toLowerCase()}`}
            size="small"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              mb: 3,
              width: { xs: "100%", sm: 320 },
              "& .MuiOutlinedInput-root": { bgcolor: COLORS.WhiteBackground, borderRadius: 2.5 },
            }}
          />
        )}

        {initialLoad ? (
          <Box sx={gridSx}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Box key={i} sx={{ ...cardShellSx, cursor: "default" }}>
                <Skeleton variant="rectangular" animation="wave" sx={{ aspectRatio: "16 / 10", height: "auto" }} />
                <Box sx={{ p: 2 }}>
                  <Skeleton width="60%" height={26} />
                  <Skeleton width="35%" height={20} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : venues.length === 0 ? (
          <Box
            sx={{
              ...cardShellSx,
              cursor: "default",
              py: 8,
              px: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(COLORS.MainColor, 0.08),
                color: COLORS.MainColor,
                mb: 1,
              }}
            >
              <StorefrontOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.1rem" }}>No {LABELS.venues.toLowerCase()} yet</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Add your first {LABELS.venue.toLowerCase()} to get started.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddVenue}
              disableElevation
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2.5 }}
            >
              Add {LABELS.venue.toLowerCase()}
            </Button>
          </Box>
        ) : visibleVenues.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4 }}>
            No {LABELS.venues.toLowerCase()} match "{search.trim()}".
          </Typography>
        ) : (
          <Box sx={gridSx}>
            {visibleVenues.map((business, idx) => {
              const businessName =
                business.name && business.name.trim()
                  ? business.name
                  : "Unnamed Business";
              return (
                <Box
                  key={businessName + idx}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open ${businessName}`}
                  onClick={() => handleBusinessClick(business)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleBusinessClick(business);
                    }
                  }}
                  sx={{
                    ...cardShellSx,
                    "@keyframes venueCardIn": {
                      from: { opacity: 0, transform: "translateY(10px)" },
                      to: { opacity: 1, transform: "none" },
                    },
                    animation: "venueCardIn 0.4s cubic-bezier(0.2, 0, 0, 1) both",
                    animationDelay: `${Math.min(idx, 10) * 45}ms`,
                    "&:hover, &:focus-visible": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 14px 32px rgba(15, 23, 42, 0.12)",
                      borderColor: alpha(COLORS.MainColor, 0.35),
                    },
                    "&:focus-visible": { outline: `2px solid ${COLORS.MainColor}`, outlineOffset: 2 },
                    "&:hover .venue-cover-img": { transform: "scale(1.05)" },
                    "&:hover .venue-open-arrow": { transform: "translateX(4px)", opacity: 1 },
                  }}
                >
                  <VenueCover
                    src={business.image}
                    name={businessName}
                    sx={{ aspectRatio: "16 / 10" }}
                  />
                  <Box
                    sx={{
                      px: 2.5,
                      py: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "1.15rem",
                          color: COLORS.typographyColor,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {businessName}
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                        Open dashboard
                      </Typography>
                    </Box>
                    <ArrowForwardIcon
                      className="venue-open-arrow"
                      sx={{
                        color: COLORS.MainColor,
                        fontSize: "1.2rem",
                        opacity: 0.6,
                        transition: "transform 0.25s, opacity 0.25s",
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const gridSx = {
  display: "grid",
  gap: { xs: 2.5, sm: 3.5 },
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, 1fr)",
    lg: "repeat(3, 1fr)",
  },
};

const cardShellSx = {
  bgcolor: COLORS.WhiteBackground,
  borderRadius: 4,
  border: "1px solid",
  borderColor: "divider",
  overflow: "hidden",
  cursor: "pointer",
  outline: "none",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  transition: "transform 0.25s cubic-bezier(0.2, 0, 0, 1), box-shadow 0.25s, border-color 0.25s",
};

export default Home;
